import { AxiosResponse } from "axios";
import { IOptions } from "../../api-sdk";
import BaseService from "../baseService";
import {
  IChangePasswordPayload,
  IForgotPasswordPayload,
  IGenerateQrCode,
  IGenerateQrCodeResponse,
  ILoginPayload,
  IReadQrCodeStatusPayload,
  IReadQrCodeStatusResponse,
  IRefreshTokenResponse,
  IRemoveSessionPayload,
  IResetPasswordPayload,
  ISendOTPPayload,
  ISendOTPResponse,
  ISignUpPayload,
  IVerifyOtpPayload,
  IVerifyQrCodePayload,
  IWhoami,
} from "./auth.types";

class AuthService extends BaseService {
  constructor(options: IOptions) {
    super(options);
  }

  /**
   * Send OTP to the user's phone
   * @param payload phone and optional recaptcha token
   * @returns response from the server
   */
  async sendOTP(
    payload: ISendOTPPayload
  ): Promise<AxiosResponse<ISendOTPResponse>> {
    return this.request({
      method: "POST",
      url: "/auth/login?provider=phone",
      data: payload,
    });
  }

  /**
   * Logins the user with the given email and password
   * @param payload email and password
   * @returns response from the server
   */
  async login(payload: ILoginPayload) {
    return this.request({
      method: "POST",
      url: "/auth/login?provider=email",
      data: payload,
    });
  }

  /**
   * Verify the OTP sent to the user
   * @param payload the otp hash and the otp sent to the user
   * @returns response from the server
   */
  async verifyOTP(payload: IVerifyOtpPayload) {
    return this.request({
      method: "POST",
      url: "/auth/verify-otp",
      data: payload,
    });
  }

  /**
   * Sign up a user
   * @param payload name, email, and password
   * @returns response from the server
   */
  async signUp(payload: ISignUpPayload) {
    return this.request({
      method: "POST",
      url: "/auth/register",
      data: payload,
    });
  }

  /**
   * Forgot password
   * @param payload email
   * @returns response from the server
   */
  async forgotPassword(payload: IForgotPasswordPayload) {
    return this.request({
      method: "POST",
      url: "/auth/forgot-password",
      data: payload,
    });
  }

  /**
   * Reset the user's password
   * @param payload the id of the user, token from the forgot password email, and the new password
   * @returns response from the server
   */
  async resetPassword(payload: IResetPasswordPayload) {
    return this.request({
      method: "POST",
      url: `/auth/reset-password/${payload.id}`,
      data: {
        token: payload.token,
        password: payload.password,
      },
    });
  }

  /**
   * Retrieves the authenticated user's information.
   *
   * @returns An object containing the user's details such as id, name, email, role, and subscription information.
   */
  async whoamI(headers?: Record<string, string>): Promise<{ data: IWhoami }> {
    return this.request({
      method: "GET",
      url: "/auth/whoami",
      headers,
    });
  }

  /**
   * Logs out the user
   * @returns response from the server
   */
  async logout(headers?: Record<string, string>) {
    return this.request({
      method: "POST",
      url: "/auth/logout",
      headers,
    });
  }

  /**
   * Removes a user session based on the provided token ID.
   * @param payload - An object containing the token ID of the session to be removed.
   * @returns Response from the server indicating the result of the session removal.
   */

  async removeSession(
    payload: IRemoveSessionPayload,
    headers?: Record<string, string>
  ) {
    return this.request({
      method: "PATCH",
      url: "/auth/self/remove-session",
      data: payload,
      headers,
    });
  }
  /**
   * Changes the user's password.
   * @param payload - An object containing the current password and the new password.
   * @returns Response from the server indicating the result of the password change.
   */
  async changePassword(payload: IChangePasswordPayload) {
    return this.request({
      method: "PATCH",
      url: "/auth/self/change-password",
      data: payload,
    });
  }

  /**
   * Generate the new QR code for login using the existing session.
   * @param payload - User's device_id
   * @returns Response from the server containing the new QR code data.
   */
  async generateQrCode(
    payload: IGenerateQrCode
  ): Promise<AxiosResponse<IGenerateQrCodeResponse>> {
    return this.request({
      method: "POST",
      url: "/auth/login/qr/generate",
      data: payload,
    });
  }

  /**
   * Read the status of the QR code and tokens if available.
   * @param payload - User's device_id and token
   * @returns Response from the server indicating the status of the QR code and tokens.
   */
  async readQrCodeStatus(
    payload: IReadQrCodeStatusPayload
  ): Promise<AxiosResponse<IReadQrCodeStatusResponse>> {
    return this.request({
      method: "POST",
      url: "/auth/login/qr/status",
      data: payload,
    });
  }

  /**
   * Verify the QR code login using the existing session.
   * @param payload - User's device_id and token
   * @returns Response from the server containing the QR code login verification data.
   */
  async verifyQrCodeLogin(
    payload: IVerifyQrCodePayload
  ): Promise<AxiosResponse<{ id: string }>> {
    return this.request({
      method: "POST",
      url: "/auth/login/qr/verify",
      data: payload,
    });
  }

  /**
   * Exchange a refresh token for a new access token (and rotated refresh token).
   *
   * Calls `POST /auth/refresh`. When your app stores refresh tokens outside of
   * cookies (e.g. mobile or SPA with custom storage), pass the token string so it
   * is sent as the `refreshToken` request header. If you rely on httpOnly cookies
   * from a prior login, omit the argument so the browser sends cookies automatically
   * (ensure `withCredentials` is enabled on the client).
   *
   * @param refreshToken - Optional refresh token value for the `refreshToken` header.
   *   Omit when the refresh token is only present as an httpOnly cookie.
   * @returns Axios response whose `data` matches {@link IRefreshTokenResponse}
   *   (user id and new `tokens`: `accessToken`, `refreshToken`, `accessTokenDeprecated`).
   */
  async refreshToken(
    refreshToken?: string
  ): Promise<AxiosResponse<IRefreshTokenResponse>> {
    return this.request({
      method: "POST",
      url: "/auth/refresh",
      ...(refreshToken && {
        headers: {
          refreshToken,
        },
      }),
    });
  }
}

export default AuthService;
